import type { Preco, Produto, TabelaPreco } from '../db/tipos'
import { regraDoPreco, type RegraQuantidade } from './regraQuantidade'

export interface LinhaPreco {
  produto: Produto
  tabela: TabelaPreco
  preco: number
  // A regra viaja JUNTO com o preco porque as duas saem da mesma linha de
  // `precos`. Ler a regra numa segunda passada, por produto, faria a etiqueta
  // mostrar o minimo de uma tabela ao lado do preco de outra -- e o mesmo
  // produto tem minimos diferentes em cada tabela.
  regra: RegraQuantidade
  // O preco da unidade SOLTA daquela mesma linha, pela mesma razao da regra
  // acima. Nulo = esta tabela nao vende unidade solta deste produto, que e o
  // caso de quase tudo hoje. ⚠ Se este numero nao viajasse junto, o item
  // nasceria sem preco avulso, a linha iria para a fila e so o Postgres
  // recusaria -- longe demais de quem digitou.
  precoUnidade: number | null
}

// Deriva as linhas da busca do pedido: uma por (produto x tabela) com preco vivo.
// So entram produto ativo/vivo, tabela ativa/viva e preco vivo. Ordena tabelas
// preferidas (memoria do cliente) primeiro, depois por descricao e nome da tabela.
export function montarLinhasPreco(
  produtos: Produto[],
  tabelas: TabelaPreco[],
  precos: Preco[],
  termo: string,
  tabelasPreferidas: Set<string> = new Set(),
  limite = 15,
): LinhaPreco[] {
  const t = termo.trim().toLowerCase()
  if (t.length < 2) return []

  const prodPorId = new Map(
    produtos.filter((p) => p.apagado_em === null && p.ativo).map((p) => [p.id, p]),
  )
  const tabPorId = new Map(
    tabelas.filter((tb) => tb.apagado_em === null && tb.ativa).map((tb) => [tb.id, tb]),
  )

  const linhas: LinhaPreco[] = []
  for (const pr of precos) {
    if (pr.apagado_em !== null) continue
    const produto = prodPorId.get(pr.produto_id)
    const tabela = tabPorId.get(pr.tabela_preco_id)
    if (!produto || !tabela) continue
    if (!produto.codigo.toLowerCase().includes(t) && !produto.descricao.toLowerCase().includes(t)) continue
    linhas.push({
      produto, tabela, preco: pr.preco, regra: regraDoPreco(pr),
      // `?? null` pelo motivo de sempre: linha em cache de antes da 0021 le
      // `undefined`, e `undefined` viraria NaN dentro do total.
      precoUnidade: pr.preco_unidade ?? null,
    })
  }

  linhas.sort((a, b) => {
    // Codigo EXATO primeiro (23/09): o botao "Adicionar ao pedido" da ficha abre
    // a busca com o codigo, e codigo curto ("350") casa com dezenas de outros --
    // sem isto o produto certo podia cair fora do corte de `limite`.
    // `.trim()` no gravado tambem: codigo importado com espaco no fim (minor 3).
    const ea = a.produto.codigo.trim().toLowerCase() === t ? 0 : 1
    const eb = b.produto.codigo.trim().toLowerCase() === t ? 0 : 1
    if (ea !== eb) return ea - eb
    const pa = tabelasPreferidas.has(a.tabela.id) ? 0 : 1
    const pb = tabelasPreferidas.has(b.tabela.id) ? 0 : 1
    if (pa !== pb) return pa - pb
    const d = a.produto.descricao.localeCompare(b.produto.descricao, 'pt-BR')
    if (d !== 0) return d
    return a.tabela.nome.localeCompare(b.tabela.nome, 'pt-BR')
  })

  return linhas.slice(0, limite)
}
